import React, { useState, useEffect } from 'react';
import { Star, Send, Trash2, Reply, ShieldCheck, MessageSquare, Lock, Loader2, X, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ───── Star rating widget ───── */
const StarRating = ({ value, onChange, size = 16, readonly = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => {
        const active = (hover || value) >= i;
        return (
          <button
            key={i}
            type="button"
            disabled={readonly}
            onClick={() => onChange?.(i)}
            onMouseEnter={() => !readonly && setHover(i)}
            onMouseLeave={() => !readonly && setHover(0)}
            className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-transform ${!readonly && 'hover:scale-110'}`}
          >
            <Star className={`${active ? 'text-amber-400 fill-amber-400 drop-shadow-[0_0_4px_rgba(251,191,36,0.5)]' : 'text-white/15'}`}
              style={{ width: size, height: size }} />
          </button>
        );
      })}
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const PortfolioReviews = ({ groupId, accentColor = '#00E5FF' }) => {
  const [data, setData] = useState({ reviews: [], stats: { average_rating: 0, total: 0 }, viewer: null });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState('');

  const token = (() => {
    // try user token first, then staff token (for staff roles posting too)
    return localStorage.getItem('user_token') || localStorage.getItem('token') || localStorage.getItem('staff_token') || '';
  })();

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${BACKEND_URL}/api/portfolio/${groupId}/reviews`, { headers });
      setData(r.data);
    } catch {
      // silent — public endpoint
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (groupId) fetchReviews(); /* eslint-disable-next-line */ }, [groupId]);

  const submitReview = async () => {
    if (!token) { toast.error('Please sign in to leave a review'); return; }
    if (text.trim().length < 10) { toast.error('Review must be at least 10 characters'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/portfolio/${groupId}/reviews`, { rating, text: text.trim() }, { headers });
      toast.success('Thanks for your review!');
      setText(''); setRating(5); setShowForm(false);
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteReview = async (rid) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/portfolio/reviews/${rid}`, { headers });
      toast.success('Review removed');
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const hideReview = async (rid) => {
    try {
      await axios.put(`${BACKEND_URL}/api/portfolio/reviews/${rid}/hide`, {}, { headers });
      toast.success('Visibility toggled');
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to hide');
    }
  };

  const submitReply = async (rid) => {
    if (!token) { toast.error('Please sign in to reply'); return; }
    if (replyText.trim().length < 2) { toast.error('Reply too short'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/portfolio/reviews/${rid}/replies`, { text: replyText.trim() }, { headers });
      setReplyText(''); setReplyOpen(null);
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reply failed');
    }
  };

  const deleteReply = async (rid, repId) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/portfolio/reviews/${rid}/replies/${repId}`, { headers });
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete reply');
    }
  };

  const isStaff = data.viewer?.is_staff_role;
  const isLoggedIn = !!data.viewer;
  const myReview = data.reviews.find(r => r.author_username === data.viewer?.username);

  return (
    <div className="space-y-3" data-testid="portfolio-reviews-section">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider flex items-center gap-1.5"
            style={{ color: accentColor }}>
            <MessageSquare className="w-3.5 h-3.5" />
            Reviews
            {data.stats.total > 0 && (
              <span className="text-white/30 normal-case tracking-normal text-[10px] ml-1">
                · {data.stats.total} review{data.stats.total !== 1 ? 's' : ''}
              </span>
            )}
          </h3>
          {data.stats.total > 0 && (
            <div className="flex items-center gap-2 mt-1">
              <StarRating value={Math.round(data.stats.average_rating)} readonly size={14} />
              <span className="text-white/50 text-xs font-medium">{data.stats.average_rating.toFixed(1)}</span>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowForm(s => !s)}
          disabled={!isLoggedIn}
          data-testid="review-toggle-form"
          className={`text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border transition-all ${
            isLoggedIn
              ? 'border-white/15 bg-white/[0.04] text-white/70 hover:bg-white/[0.08] hover:text-white'
              : 'border-white/10 bg-white/[0.02] text-white/25 cursor-not-allowed'
          }`}
          title={isLoggedIn ? '' : 'Sign in to leave a review'}
        >
          {!isLoggedIn ? <span className="flex items-center gap-1"><Lock className="w-3 h-3" />Sign in</span> :
            myReview ? 'Edit Review' : 'Write Review'}
        </button>
      </div>

      {/* Form */}
      {showForm && isLoggedIn && (
        <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] space-y-3" data-testid="review-form">
          <div className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-wider text-white/40">Your rating</span>
            <StarRating value={rating} onChange={setRating} size={20} />
          </div>
          <textarea
            rows={3}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Share your honest experience…"
            data-testid="review-text-input"
            className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-white/30 resize-none"
            maxLength={2000}
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-white/30">{text.length}/2000 · Min 10 chars · Posted as <span className="text-white/60">@{data.viewer?.username}</span> ✓ Verified</span>
            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)} className="text-xs text-white/50 hover:text-white px-3 py-1.5">Cancel</button>
              <button
                onClick={submitReview}
                disabled={submitting || text.trim().length < 10}
                data-testid="review-submit-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium text-black bg-white hover:bg-white/90 disabled:opacity-40 disabled:cursor-not-allowed">
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Post review
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <p className="text-white/30 text-xs italic py-4 text-center">Loading reviews…</p>
      ) : data.reviews.length === 0 ? (
        <div className="text-center py-6 rounded-xl bg-white/[0.02] border border-white/[0.05]">
          <Star className="w-5 h-5 text-white/15 mx-auto mb-2" />
          <p className="text-white/30 text-xs">No reviews yet — be the first!</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {data.reviews.map(r => (
            <div key={r.id} className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-3.5" data-testid={`review-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-sm font-medium">{r.author_display_name || r.author_username}</span>
                    {r.verified && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-emerald-300">
                        <ShieldCheck className="w-3 h-3" />Verified
                      </span>
                    )}
                    <span className="text-white/25 text-[10px]">· {fmtDate(r.created_at)}</span>
                  </div>
                  <div className="mt-1"><StarRating value={r.rating} readonly size={12} /></div>
                </div>
                <div className="flex items-center gap-1">
                  {isLoggedIn && (
                    <button onClick={() => { setReplyOpen(replyOpen === r.id ? null : r.id); setReplyText(''); }}
                      data-testid={`reply-toggle-${r.id}`}
                      className="p-1 rounded hover:bg-white/[0.06] text-white/40 hover:text-white" title="Reply">
                      <Reply className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {isStaff && (
                    <button onClick={() => hideReview(r.id)}
                      className="p-1 rounded hover:bg-white/[0.06] text-white/40 hover:text-amber-400" title="Hide">
                      <EyeOff className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {r.can_delete && (
                    <button onClick={() => deleteReview(r.id)}
                      data-testid={`review-delete-${r.id}`}
                      className="p-1 rounded hover:bg-red-500/15 text-white/40 hover:text-red-400" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-2 text-white/60 text-sm leading-relaxed whitespace-pre-wrap">{r.text}</p>

              {/* Replies */}
              {r.replies?.length > 0 && (
                <div className="mt-3 pl-3 border-l border-white/[0.06] space-y-2">
                  {r.replies.map(rep => (
                    <div key={rep.id} className="text-xs" data-testid={`reply-${rep.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white/80 font-medium">{rep.author_display_name || rep.author_username}</span>
                        {rep.is_staff && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider font-bold bg-violet-500/20 text-violet-200 border border-violet-500/30">
                            <ShieldCheck className="w-2.5 h-2.5" />Starnight Staff
                          </span>
                        )}
                        <span className="text-white/25 text-[10px]">· {fmtDate(rep.created_at)}</span>
                        {rep.can_delete && (
                          <button onClick={() => deleteReply(r.id, rep.id)}
                            className="p-0.5 rounded hover:text-red-400 text-white/30 ml-auto" title="Delete reply">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <p className="text-white/55 mt-0.5">{rep.text}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply form */}
              {replyOpen === r.id && (
                <div className="mt-3 flex gap-2">
                  <input
                    type="text"
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && submitReply(r.id)}
                    placeholder={isStaff ? 'Reply as Starnight Staff…' : 'Reply…'}
                    data-testid={`reply-input-${r.id}`}
                    className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder-white/25 focus:outline-none focus:border-white/30"
                    maxLength={1000}
                  />
                  <button onClick={() => submitReply(r.id)}
                    disabled={replyText.trim().length < 2}
                    data-testid={`reply-submit-${r.id}`}
                    className="px-3 py-1.5 rounded-lg bg-white text-black text-xs font-medium disabled:opacity-40 hover:bg-white/90">
                    Send
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!isLoggedIn && (
        <p className="text-center text-white/25 text-[10px] tracking-wider mt-2">
          <Lock className="inline w-3 h-3 mr-1" />Sign in to leave a verified review
        </p>
      )}
    </div>
  );
};

export default PortfolioReviews;
